import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDateTime } from '@/lib/format'
import { idempotencyKey } from '@/lib/idempotency'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type TargetOut = Schema<'PublishTargetOut'>
type Publication = Schema<'PublicationOut'>
type Document = Schema<'ContentDocumentOut'>

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
const IST = 'Asia/Kolkata'

/**
 * The statuses a document has to have reached before publishing is a question
 * worth asking about it. `approved` and `published` are `PUBLISHABLE_STATUSES`
 * on the server; `publishing` is the state the press moves a document into
 * while the worker is running, and `failed` is where a publish that did not
 * work leaves it — both belong on a screen whose whole subject is what did and
 * did not reach the tenant's website.
 */
const SHIPPABLE = ['approved', 'publishing', 'published', 'failed'] as const

/**
 * What `GET /seo/sites/{id}/content` will return at most, from `openapi.json`,
 * where `default` and `maximum` are both 100 — so this is the cap in force even
 * though the call sends no `limit`. Named on screen because the route has no
 * cursor, and a published list that quietly stops at 100 reads as "we published
 * 100 things" (WEB-16).
 */
const DOCUMENT_LIMIT = 100

/* --- the four targets, as data ------------------------------------------------- */

interface TargetField {
  name: string
  label: string
  help?: string
  required?: boolean
  kind: 'text' | 'select' | 'number' | 'ints' | 'words' | 'switch'
  placeholder?: string
  mono?: boolean
  options?: readonly { value: string; label: string }[]
  /** Nested under `settings.fields` — Webflow's collection field slugs. */
  group?: 'fields'
}

interface TargetSpec {
  id: string
  label: string
  logo: string
  blurb: string
  credentialLabel: string
  credentialHelp: string
  fields: readonly TargetField[]
}

/**
 * Every field name, default and bound below is read off the publisher that will
 * use it — `app/seo/gitpush.py`, `wordpress.py`, `webflow.py` and `shopify.py`,
 * via `PublishTargetIn`'s own description of what each target takes. Nothing
 * here invents a setting: a key this form sends that no `Config.of` reads would
 * be written to the row and silently ignored for ever.
 */
const TARGETS: readonly TargetSpec[] = [
  {
    id: 'git',
    label: 'Git repository',
    logo: 'git',
    blurb:
      'For Next.js, Hugo, Astro — AugRank commits a Markdown file and your build publishes it.',
    credentialLabel: 'Deploy key',
    credentialHelp:
      'The private half of an SSH deploy key with write access. Paste the whole key, including the BEGIN and END lines.',
    fields: [
      {
        name: 'repo_url',
        label: 'Repository',
        required: true,
        kind: 'text',
        mono: true,
        placeholder: 'git@github.com:owner/repo.git',
        help: 'An SSH remote — that is what a deploy key authenticates against.',
      },
      { name: 'branch', label: 'Branch', kind: 'text', placeholder: 'main' },
      {
        name: 'path_template',
        label: 'Where the file goes',
        kind: 'text',
        mono: true,
        placeholder: 'content/blog/{slug}.md',
        help: 'Relative to the repository root, and it must contain {slug} — without it every article overwrites the same file.',
      },
      {
        name: 'host_key',
        label: 'Host key',
        kind: 'text',
        mono: true,
        placeholder: 'ssh-ed25519 AAAA…',
        help: 'Run ssh-keyscan -t ed25519 on the repository’s host and paste the part after the host name. Required for an SSH remote, so the push can verify which server it reached.',
      },
      {
        name: 'url_template',
        label: 'Public URL',
        kind: 'text',
        placeholder: 'https://example.com/blog/{slug}',
        help: 'Optional. A push cannot know how your site routes, so AugRank shows no link at all rather than a wrong one.',
      },
      // Rendered although `PublishTargetIn`'s description does not list them,
      // and that is the point: `GitConfig` reads both, and `write_target`
      // replaces a target's settings object wholesale. A form that omitted a
      // field the publisher uses would silently drop whatever was there.
      { name: 'author_name', label: 'Commit author', kind: 'text', placeholder: 'AugRank' },
      {
        name: 'author_email',
        label: 'Commit email',
        kind: 'text',
        placeholder: 'seo@augrank.com',
        help: 'Whose name the commit is made in. It shows in your repository’s history.',
      },
    ],
  },
  {
    id: 'wordpress',
    label: 'WordPress',
    logo: 'W',
    blurb: 'Posts through the WordPress REST API, as a user you create for AugRank.',
    credentialLabel: 'Application password',
    credentialHelp:
      'Created under Users → Profile → Application Passwords in WordPress. Not the account’s login password.',
    fields: [
      {
        name: 'base_url',
        label: 'Site URL',
        required: true,
        kind: 'text',
        placeholder: 'https://example.com',
        help: 'The address of the site itself, not the wp-json path.',
      },
      {
        name: 'username',
        label: 'Username',
        required: true,
        kind: 'text',
        help: 'The account the application password belongs to.',
      },
      {
        name: 'status',
        label: 'Publish as',
        kind: 'select',
        options: [
          { value: 'draft', label: 'Draft — lands in your editorial queue' },
          { value: 'publish', label: 'Published — goes live immediately' },
        ],
        help: 'Approval is a separate gate either way. This is only what WordPress does with the post once AugRank sends it.',
      },
      {
        name: 'category_ids',
        label: 'Categories',
        kind: 'ints',
        placeholder: '12, 34',
        help: 'WordPress term ids, separated by commas. Leave empty for the site’s default category.',
      },
      {
        name: 'author_id',
        label: 'Author id',
        kind: 'number',
        help: 'Leave empty to post as the application password’s own user, which is the only account that always has permission.',
      },
    ],
  },
  {
    id: 'webflow',
    label: 'Webflow',
    logo: 'Wf',
    blurb: 'CMS collection items, created live rather than staged.',
    credentialLabel: 'API token',
    credentialHelp: 'A Webflow site token with CMS write access.',
    fields: [
      {
        name: 'collection_id',
        label: 'Collection id',
        required: true,
        kind: 'text',
        mono: true,
        help: 'The 24-character id in the collection’s URL in the Webflow designer.',
      },
      {
        name: 'title',
        group: 'fields',
        label: 'Title field',
        kind: 'text',
        mono: true,
        placeholder: 'name',
      },
      {
        name: 'slug',
        group: 'fields',
        label: 'Slug field',
        kind: 'text',
        mono: true,
        placeholder: 'slug',
      },
      {
        name: 'body',
        group: 'fields',
        label: 'Body field',
        kind: 'text',
        mono: true,
        placeholder: 'post-body',
      },
      {
        name: 'summary',
        group: 'fields',
        label: 'Summary field',
        kind: 'text',
        mono: true,
        placeholder: 'post-summary',
        help: 'The four slugs above are Webflow’s own blog template. Change one only if your collection renamed it.',
      },
      {
        name: 'url_template',
        label: 'Public URL',
        kind: 'text',
        placeholder: 'https://example.com/blog/{slug}',
        help: 'Webflow does not return a public address for a CMS item, so this is the only way AugRank can offer a link to it.',
      },
    ],
  },
  {
    id: 'shopify',
    label: 'Shopify',
    logo: 'S',
    blurb: 'Blog articles through the Shopify Admin API.',
    credentialLabel: 'Admin API token',
    credentialHelp: 'A custom app’s Admin API access token with write_content scope.',
    fields: [
      {
        name: 'shop_domain',
        label: 'Shop domain',
        required: true,
        kind: 'text',
        mono: true,
        placeholder: 'example.myshopify.com',
        help: 'The myshopify.com form, not the storefront’s custom domain.',
      },
      {
        name: 'blog_id',
        label: 'Blog id',
        required: true,
        kind: 'text',
        mono: true,
        help: 'The numeric id at the end of the blog’s admin URL.',
      },
      { name: 'tags', label: 'Tags', kind: 'words', placeholder: 'guides, pricing' },
      { name: 'author', label: 'Byline', kind: 'text', placeholder: 'AugRank' },
      {
        name: 'published',
        label: 'Visible as soon as it is created',
        kind: 'switch',
        help: 'Off leaves the article in the store’s blog as unpublished.',
      },
    ],
  },
]

/** The chosen target, falling back to git — which is the first of the four and
 *  the one a static-site tenant is likeliest to want. `TARGETS` is a literal, so
 *  the fallback is total by construction; the throw is what keeps that provable
 *  to the type checker without a non-null assertion. */
function specFor(id: string | null): TargetSpec {
  const found = TARGETS.find((target) => target.id === id) ?? TARGETS[0]
  if (found === undefined) throw new Error('TARGETS is empty')
  return found
}

/* --- the form's values, which are strings until they are sent ------------------ */

type FormValue = string | boolean
type Form = Record<string, FormValue>

function keyOf(field: TargetField): string {
  return field.group === undefined ? field.name : `${field.group}.${field.name}`
}

/**
 * A stored settings object as the form holds it.
 *
 * Everything is a string except a switch, because that is what an `<input>`
 * gives back, and converting on the way in and out in one place is what keeps
 * `1, 2` from being sent as the string `"1, 2"` to a field the server parses as
 * a list of ints. A switch defaults to **on**, matching `ShopifyConfig.published`
 * — `raw.get("published") is not False` — so an unset field and a ticked box
 * mean the same thing on both sides.
 */
function formOf(spec: TargetSpec, settings: Record<string, unknown>): Form {
  const form: Form = {}
  for (const field of spec.fields) {
    const source =
      field.group === undefined
        ? settings
        : ((settings[field.group] as Record<string, unknown> | undefined) ?? {})
    const value = source[field.name]
    if (field.kind === 'switch') {
      form[keyOf(field)] = value !== false
    } else if (Array.isArray(value)) {
      form[keyOf(field)] = value.map((item) => String(item)).join(', ')
    } else {
      form[keyOf(field)] =
        typeof value === 'string' || typeof value === 'number' ? String(value) : ''
    }
  }
  return form
}

/** The form as `PublishTargetIn.settings`. An empty field is omitted, never sent
 *  as `""` — the publishers read a missing key as "use the default" and an empty
 *  string as a value, and the two produce different configurations. */
function settingsOf(spec: TargetSpec, form: Form): Record<string, unknown> {
  const settings: Record<string, unknown> = {}
  const nested: Record<string, string> = {}
  for (const field of spec.fields) {
    const raw = form[keyOf(field)]
    if (field.kind === 'switch') {
      settings[field.name] = raw === true
      continue
    }
    const text = typeof raw === 'string' ? raw.trim() : ''
    if (text === '') continue
    let value: unknown = text
    if (field.kind === 'number') value = Number(text)
    if (field.kind === 'ints') {
      value = text
        .split(',')
        .map((item) => Number(item.trim()))
        .filter((item) => Number.isFinite(item))
    }
    if (field.kind === 'words') {
      value = text
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '')
    }
    if (field.group === undefined) settings[field.name] = value
    else nested[field.name] = String(value)
  }
  if (Object.keys(nested).length > 0) settings.fields = nested
  return settings
}

/* --- the screen ---------------------------------------------------------------- */

/**
 * Where we publish — S-D5's `seo.html#targets`, built in S-G5 over S-B28.
 *
 * **The five endpoints in `app/v1/seo_publish.py` had no caller at all.** They
 * shipped tested and reachable, and until this screen existed the approval queue
 * told the tenant in as many words that *"pushing it to your website is a
 * separate step and is not built yet"* — audit row §A1, and §V3 as the tenant
 * experienced it.
 *
 * WHY IT IS A ROUTE UNDER `/seo` AND NOT A NINTH SETTINGS PANEL
 * ---------------------------------------------------------------
 * The same argument `RunTriggers` makes: **a publish target is per site and the
 * settings screen is per organisation.** An agency with a staging domain and a
 * client's live domain needs two different answers, and `sites.publish_target`
 * is the column that says so. S-D3 approved eight settings panels and eight SEO
 * sections; adding a ninth of either would be inventing information architecture
 * nobody drew, which is the reason `panels.ts` folded webhooks into
 * Notifications rather than adding a row.
 *
 * So it has **no sidebar entry** and two ways in instead, which is what the
 * audit's correction to the brief asks for: a card in Settings → Connections,
 * because that is where a tenant looks for *"what is AugRank connected to"* and
 * where `configured_target`'s own refusal sends them; and a link from the
 * approval queue, because that is the moment the question actually arises.
 *
 * WHAT IS NEVER ON THIS SCREEN
 * ------------------------------
 * **A credential, in any form.** Not masked, not a prefix, not a length. CLAUDE.md's
 * Secrets invariant is absolute and `PublishTargetOut` is built for it: a
 * `credential_set` boolean and the Parameter Store path, which is an address
 * rather than a secret. The field below is write-only — it is empty on arrival,
 * empty after a save, and omitted from the request when the tenant did not type
 * in it, which is what `PublishTargetIn.credential` means by *"omit to leave the
 * stored one unchanged"*.
 *
 * **Two mockup controls are deliberately absent**, because no endpoint stands
 * behind either and half of one is worse than none. *Send a test post* would
 * have to publish something real to somebody's live site. *Disconnect* would
 * have to delete a target and its credential, and `write_target` only ever
 * merges — there is no DELETE. Both are named in `PROGRESS.md` rather than
 * rendered as buttons that do nothing.
 */
export function PublishTargets(): JSX.Element {
  const { role } = useActiveTenant()
  const configurable = may(role, 'tenant:update')
  const publishable = may(role, 'content:approve')
  const [siteId, setSiteId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const selectedId = siteId ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null

  if (sites.state.status === 'loading') return <Loading label="Loading your sites" />
  if (sites.state.status === 'error') {
    return <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Where we publish</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <select
            className="ar-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {site === null ? (
        <div className="ar-card">
          <div className="ar-card__body">
            <Empty
              title="No site yet"
              body="Add your website first. Until AugRank knows where your pages live there is nowhere for an approved article to go."
            />
          </div>
        </div>
      ) : (
        <>
          <TargetSetup
            key={site.id}
            siteId={site.id}
            domain={site.domain}
            configurable={configurable}
          />
          <PublishedWork key={`${site.id}-published`} siteId={site.id} publishable={publishable} />
        </>
      )}
    </div>
  )
}

/* --- the configuration --------------------------------------------------------- */

/**
 * One site's target, and the other three offered beside it.
 *
 * Its own component so switching site remounts it and a half-typed WordPress
 * password cannot survive into another site's form — the same reason
 * `RunTriggers` splits `TriggerCards` out.
 *
 * The read is re-issued whenever the tenant picks a different target, because
 * `?target=` is what returns *that* target's saved settings and *that* target's
 * `credential_set`. Without it the screen could list `configured_targets` and
 * say nothing about any of them, and switching back to a target would mean
 * retyping a configuration the row already holds.
 */
function TargetSetup({
  siteId,
  domain,
  configurable,
}: {
  siteId: string
  domain: string
  configurable: boolean
}): JSX.Element {
  const toast = useToast()
  const [chosen, setChosen] = useState<string | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [credential, setCredential] = useState('')
  const [seededFrom, setSeededFrom] = useState<TargetOut | null>(null)

  const target = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/publish-targets/{site_id}', {
          params: {
            path: { site_id: siteId },
            ...(chosen === null ? {} : { query: { target: chosen } }),
          },
          signal,
        }),
      ),
    [siteId, chosen],
  )

  const save = useMutation((body: { target: string; settings: Record<string, unknown> }) =>
    call(() =>
      api.PUT('/api/v1/seo/publish-targets/{site_id}', {
        params: { path: { site_id: siteId } },
        body: credential.trim() === '' ? body : { ...body, credential: credential.trim() },
      }),
    ),
  )

  const stored: TargetOut | null = target.state.data
  //: Which target the response in hand actually describes.
  const answered = stored?.viewing ?? stored?.target ?? null
  // **In flight until the answer is about the target on screen**, and this is
  // not the same question as `state.status === 'loading'`. Pressing *Switch
  // back* changes `chosen` at once, so there is a render where the new target's
  // labels are up and the query has not been re-issued yet — the old target's
  // response is still the data. Treating that render as ready puts a *Deploy
  // key* box under a *WordPress* heading for a frame, which is the one confusion
  // a credential field must never cause. Observed in a real browser against a
  // real API, not in the jsdom suite, where the stub resolves in a microtask.
  const pending = target.state.status === 'loading' || (chosen !== null && answered !== chosen)
  const spec = specFor(pending ? chosen : answered)

  // Seeded during render rather than in an effect, on `BrandVoicePanel`'s
  // argument: an effect paints the defaults for one frame first, which shows a
  // repository address that is not the tenant's.
  //
  // **Keyed on the response object, not on the target's name**, and the
  // difference is a bug this shipped with once. Pressing *Switch back* changes
  // `chosen` immediately, so there is one render where the new target's fields
  // are on screen and the *old* target's response is still the query's data. A
  // key made of the chosen target and the request status matches in that render,
  // seeds the form from the wrong body, and then matches again when the real
  // answer lands — so the fields stay empty for ever. `useQuery` hands back a
  // new object per response, so identity seeds exactly once per answer, and the
  // spec is taken from the response's own `viewing` rather than from `chosen`
  // for the same reason: the form must describe the body it was filled from.
  if (stored !== null && stored !== seededFrom) {
    setSeededFrom(stored)
    setForm(formOf(specFor(stored.viewing ?? stored.target), stored.settings ?? {}))
    setCredential('')
  }

  const values = form ?? formOf(spec, {})
  const isCurrent = stored?.target === spec.id
  const everConfigured = (stored?.configured_targets ?? []).includes(spec.id)

  return (
    <>
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>{spec.label}</h3>
          <span className="ar-card__spacer" />
          {isCurrent ? (
            <span className="ar-badge ar-badge--success">✓ Publishing here</span>
          ) : everConfigured ? (
            <span className="ar-badge ar-badge--neutral">Saved, not in use</span>
          ) : (
            <span className="ar-badge ar-badge--neutral">Not set up</span>
          )}
        </div>
        <div className="ar-card__body ar-stack">
          <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
            {spec.blurb} Approved articles for <strong>{domain}</strong> go here.
          </p>

          {pending ? (
            <Loading label="Loading this site’s publish target" />
          ) : target.state.status === 'error' ? (
            <ErrorState failure={target.state.failure} onRetry={target.refetch} />
          ) : (
            <>
              <InlineError failure={save.state.failure} />

              {spec.fields.map((field) => (
                <SettingField
                  key={keyOf(field)}
                  field={field}
                  value={values[keyOf(field)] ?? ''}
                  disabled={!configurable}
                  onChange={(next) => {
                    setForm({ ...values, [keyOf(field)]: next })
                    save.reset()
                  }}
                />
              ))}

              <Field
                label={spec.credentialLabel}
                help={`${spec.credentialHelp} It is written to AWS Parameter Store as an encrypted string, never to AugRank’s database, and it is never shown again — not even to you.`}
              >
                {(aria) => (
                  <input
                    className="ar-input ar-mono"
                    type="password"
                    autoComplete="new-password"
                    {...aria}
                    disabled={!configurable}
                    value={credential}
                    placeholder={
                      stored?.credential_set === true
                        ? 'Stored — leave blank to keep it'
                        : 'Not stored yet'
                    }
                    onChange={(event) => {
                      setCredential(event.currentTarget.value)
                      save.reset()
                    }}
                  />
                )}
              </Field>

              <p className="app-pub__note">
                {stored?.credential_set === true
                  ? `A credential is stored for ${spec.label}. Leave the box empty to keep it, or type a new one to replace it.`
                  : `No credential is stored for ${spec.label} yet, so a publish to it would be refused.`}
                {stored?.credential_path ? (
                  <>
                    {' '}
                    It lives at <code className="ar-mono">{stored.credential_path}</code> — an
                    address, not the secret.
                  </>
                ) : null}
              </p>
            </>
          )}
        </div>
        {configurable && !pending && target.state.status === 'ready' ? (
          <div className="ar-card__footer">
            <Button
              tone="primary"
              busy={save.pending}
              busyLabel="Saving"
              onClick={() => {
                void (async () => {
                  const result = await save.run({
                    target: spec.id,
                    settings: settingsOf(spec, values),
                  })
                  if (!result.ok) return
                  setCredential('')
                  target.refetch()
                  toast.show({
                    kind: 'success',
                    title: `${domain} publishes to ${spec.label}`,
                    body: 'Approved articles will be pushed here from now on.',
                  })
                })()
              }}
            >
              {isCurrent ? 'Save' : `Publish to ${spec.label}`}
            </Button>
          </div>
        ) : null}
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Somewhere else</h3>
        </div>
        <div className="ar-card__body ar-stack ar-stack--tight">
          {TARGETS.filter((other) => other.id !== spec.id).map((other) => {
            const saved = (stored?.configured_targets ?? []).includes(other.id)
            return (
              <div className="app-conn" key={other.id}>
                <span className="app-conn__logo" aria-hidden="true">
                  {other.logo}
                </span>
                <span className="app-conn__body">
                  <strong>{other.label}</strong>
                  <span>
                    {saved
                      ? 'Set up already — AugRank kept these settings when you switched away.'
                      : other.blurb}
                  </span>
                </span>
                <Button
                  onClick={() => {
                    setChosen(other.id)
                  }}
                >
                  {saved ? 'Switch back' : 'Set up'}
                </Button>
              </div>
            )
          })}
        </div>
      </section>
    </>
  )
}

/** One setting, in whichever control its kind calls for. */
function SettingField({
  field,
  value,
  disabled,
  onChange,
}: {
  field: TargetField
  value: FormValue
  disabled: boolean
  onChange: (next: FormValue) => void
}): JSX.Element {
  if (field.kind === 'switch') {
    return (
      <label className="app-pub__switch">
        <input
          type="checkbox"
          checked={value === true}
          disabled={disabled}
          onChange={(event) => {
            onChange(event.currentTarget.checked)
          }}
        />
        <span>
          <strong>{field.label}</strong>
          {field.help === undefined ? null : <span className="ar-subtle">{field.help}</span>}
        </span>
      </label>
    )
  }
  return (
    <Field label={field.label} help={field.help} required={field.required}>
      {(aria) =>
        field.kind === 'select' ? (
          <select
            className="ar-select"
            {...aria}
            disabled={disabled}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => {
              onChange(event.currentTarget.value)
            }}
          >
            {(field.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            className={field.mono === true ? 'ar-input ar-mono' : 'ar-input'}
            {...aria}
            disabled={disabled}
            inputMode={field.kind === 'number' ? 'numeric' : undefined}
            placeholder={field.placeholder}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => {
              onChange(event.currentTarget.value)
            }}
          />
        )
      }
    </Field>
  )
}

/* --- what actually went out ---------------------------------------------------- */

/**
 * The documents that have reached a publishable state, and where each one went.
 *
 * **A failed publication is shown, deliberately.** `read_publications`' own
 * docstring makes the argument and this screen is where it lands: a `failed` row
 * is what tells a tenant the article they approved is *not* on their site, and a
 * list that waited for success would agree with a publish that never happened.
 * The mockup draws the same thing as a *"Last publish failed"* alert.
 */
function PublishedWork({
  siteId,
  publishable,
}: {
  siteId: string
  publishable: boolean
}): JSX.Element {
  const documents = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/content', {
          params: { path: { site_id: siteId }, query: { status: [...SHIPPABLE] } },
          signal,
        }),
      ),
    [siteId],
  )

  const rows = documents.state.data?.documents ?? []
  // A full page is the only signal that there may be more — the same reading
  // `Suppression` makes of its own `LIMIT n` with no offset.
  const capped = rows.length >= DOCUMENT_LIMIT

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h3>What we have published</h3>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {documents.state.status === 'loading' ? (
          <Loading label="Loading what has been approved" />
        ) : documents.state.status === 'error' ? (
          <ErrorState failure={documents.state.failure} onRetry={documents.refetch} />
        ) : rows.length === 0 ? (
          <Empty
            title="Nothing approved yet"
            body="Approved articles appear here with what happened to each of them. Until one is approved there is nothing to push."
          />
        ) : (
          rows.map((document) => (
            <DocumentRow key={document.id} document={document} publishable={publishable} />
          ))
        )}
        {capped ? (
          <p className="ar-subtle ar-text-body-sm" style={{ margin: 0 }}>
            Showing the {DOCUMENT_LIMIT} most recent, which is as many as this list will load at
            once.
          </p>
        ) : null}
      </div>
    </section>
  )
}

const DOCUMENT_STATUS: Record<string, { tone: string; label: string }> = {
  approved: { tone: 'ar-badge--info', label: 'Approved' },
  publishing: { tone: 'ar-badge--warning', label: 'Publishing' },
  published: { tone: 'ar-badge--success', label: 'Published' },
  failed: { tone: 'ar-badge--danger', label: 'Failed' },
}

const PUBLICATION_STATE: Record<string, { tone: string; label: string }> = {
  pending: { tone: 'ar-badge--warning', label: 'In progress' },
  published: { tone: 'ar-badge--success', label: 'Live' },
  failed: { tone: 'ar-badge--danger', label: 'Failed' },
  rolled_back: { tone: 'ar-badge--neutral', label: 'Withdrawn' },
}

/**
 * One approved document, and its publications on request.
 *
 * The publications are fetched when the row is opened rather than for every row
 * at once: `GET /content/{id}/publications` is per document, so a list of thirty
 * would be thirty requests to render a screen most of whose rows nobody expands.
 */
function DocumentRow({
  document,
  publishable,
}: {
  document: Document
  publishable: boolean
}): JSX.Element {
  const toast = useToast()
  const [open, setOpen] = useState(false)

  const publications = useQuery(
    (signal) =>
      open
        ? call(() =>
            api.GET('/api/v1/seo/content/{content_id}/publications', {
              params: { path: { content_id: document.id } },
              signal,
            }),
          )
        : Promise.resolve({ ok: true as const, data: null }),
    [open, document.id],
  )

  const publish = useMutation(() =>
    call(() =>
      api.POST('/api/v1/seo/content/{content_id}/publish', {
        params: {
          path: { content_id: document.id },
          header: { 'Idempotency-Key': idempotencyKey('publish', document.id) },
        },
        body: {},
      }),
    ),
  )

  const rollback = useMutation((publicationId: string) =>
    call(() =>
      api.POST('/api/v1/seo/publications/{publication_id}/rollback', {
        params: {
          path: { publication_id: publicationId },
          header: { 'Idempotency-Key': idempotencyKey('rollback', publicationId) },
        },
      }),
    ),
  )

  const badge = DOCUMENT_STATUS[document.status] ?? { tone: 'ar-badge--neutral', label: 'Draft' }
  const rows: Publication[] = publications.state.data?.publications ?? []

  return (
    <div className="app-pub">
      <div className="app-pub__head">
        <span className="app-pub__title">
          <strong>{document.title ?? 'Untitled draft'}</strong>
          <span className="ar-subtle">{document.slug ?? 'no slug yet'}</span>
        </span>
        <span className={`ar-badge ${badge.tone}`}>{badge.label}</span>
        <Button
          onClick={() => {
            setOpen(!open)
          }}
        >
          {open ? 'Hide history' : 'Publishing history'}
        </Button>
        {publishable ? (
          <Button
            tone="primary"
            busy={publish.pending}
            busyLabel="Queueing"
            onClick={() => {
              void (async () => {
                const result = await publish.run(undefined)
                if (!result.ok) return
                setOpen(true)
                publications.refetch()
                toast.show({
                  kind: 'success',
                  title: `Queued for ${result.data.target}`,
                  body: 'AugRank publishes in the background. Reopen the history in a moment to see how it went.',
                })
              })()
            }}
          >
            Publish
          </Button>
        ) : null}
      </div>

      <InlineError failure={publish.state.failure ?? rollback.state.failure} />

      {!open ? null : publications.state.status === 'loading' ? (
        <Loading label="Loading where this went" />
      ) : publications.state.status === 'error' ? (
        <ErrorState failure={publications.state.failure} onRetry={publications.refetch} />
      ) : rows.length === 0 ? (
        <p className="app-pub__note">
          This has never been pushed anywhere. Press <strong>Publish</strong> to send it to the
          target set up above.
        </p>
      ) : (
        <ul className="app-pub__list">
          {rows.map((row) => {
            const state = PUBLICATION_STATE[row.state] ?? {
              tone: 'ar-badge--neutral',
              label: row.state,
            }
            return (
              <li className="app-pub__row" key={row.id}>
                <span className={`ar-badge ${state.tone}`}>{state.label}</span>
                <span className="app-pub__where">
                  <strong>{row.target}</strong>
                  {row.external_url ? (
                    <a className="ar-link" href={row.external_url} rel="noreferrer noopener">
                      {row.external_url}
                    </a>
                  ) : (
                    <span className="ar-subtle">
                      {row.external_id ?? 'no address — the target did not return one'}
                    </span>
                  )}
                  <span className="ar-subtle">
                    {row.published_at ? `Live since ${formatDateTime(row.published_at, IST)}` : ''}
                    {row.rolled_back_at
                      ? `Withdrawn ${formatDateTime(row.rolled_back_at, IST)}`
                      : ''}
                    {row.attempts > 1 ? ` · ${String(row.attempts)} attempts` : ''}
                  </span>
                  {row.last_error !== null && row.last_error !== undefined ? (
                    <span className="app-pub__error">{row.last_error}</span>
                  ) : null}
                </span>
                {publishable && row.state === 'published' ? (
                  <Button
                    tone="quiet"
                    busy={rollback.pending}
                    busyLabel="Withdrawing"
                    onClick={() => {
                      void (async () => {
                        const result = await rollback.run(row.id)
                        if (!result.ok) return
                        publications.refetch()
                        toast.show({
                          kind: 'success',
                          title: result.data.removed ? 'Taken down' : 'Nothing to take down',
                          body: result.data.detail,
                        })
                      })()
                    }}
                  >
                    Roll back
                  </Button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
